<script setup lang="ts">

import { computed } from 'vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import PlayerName from '@/components/Objects/PlayerName.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { CDN } from '@/models/cdn.ts'

/**
 * Stores / models
 */
const playerRelationStore = usePlayerRelationStore()
const gameStore = useGameStore()
const allianceStore = useAllianceStore()

/**
 * Functions
 */
const leaderboardData = computed(() => {
  if (!gameStore.leaderboard || !Array.isArray(gameStore.leaderboard)) {
    return []
  }
  return gameStore.leaderboard
})

const loadPlayer = (playerId: number) => {
  if (playerId === 0 || playerId === gameStore.player?.id) {
    return
  }
  
  const relation = playerRelationStore.findRelationByPlayerId(playerId)
  if (relation.relation === 'unknown') {
    return
  }
  
  gameStore.modalType = 'requestAudience'
  gameStore.modalData = {
    relation: relation
  }
}

const getAllianceName = (playerId: number) => {
  // If this is the current player
  if (playerId === gameStore.player?.id && gameStore.player?.allianceId) {
    const alliance = allianceStore.getAlliance(gameStore.player.allianceId)
    return alliance?.name || ''
  }
  
  // For other players, check all alliances for membership
  for (const alliance of allianceStore.alliances.values()) {
    if (alliance.members && alliance.members.includes(playerId)) {
      return alliance.name
    }
  }
  return ''
}

const getPlayerCountry = (playerId: number) => {
  if (playerId === gameStore.player?.id) {
    return gameStore.player?.country || ''
  }
  return playerRelationStore.getCountryByPlayerId(playerId) || ''
}

const isPlayerDefeated = (item: any) => {
  // Assuming the leaderboard item has a population field
  return item.defeated  === 1
}

const isPlayerInactive = (item: any) => {
  return item.inactive === 1
}

</script>

<template>
  <div class="p-2">    
    <div class="pb-2 w-full xl:w-2/3 mx-auto">
      <StandardCard title="Leaderboard" top-padding="0">
        These empires are ranked by their total population.
      </StandardCard>
      <StandardCard :no-padding="true" top-padding="2">
        <div v-if="leaderboardData.length === 0" class="p-8 text-center text-slate-400">
          No leaderboard data available...
        </div>
        <div v-else class="overflow-x-auto rounded-t">
          <table class="w-full">
            <thead>
              <tr class="bg-slate-700 rounded-t">
                <th class="px-4 py-3 text-left text-xs text-slate-300 tracking-wider">
                  Rank
                </th>
                <th class="px-2 py-3 text-center text-xs text-slate-300 tracking-wider">
                  Flag
                </th>
                <th class="px-4 py-3 text-left text-xs text-slate-300 tracking-wider">
                  Empire
                </th>
                <th class="px-4 py-3 text-left text-xs text-slate-300 tracking-wider">
                  Alliance
                </th>
                <th class="px-2 py-3 text-center text-xs text-slate-300 tracking-wider">
                  Inactive
                </th>
                <th v-if="gameStore.combatMode !== 'FFA'" class="px-2 py-3 text-center text-xs text-slate-300 tracking-wider">
                  Protected
                </th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="item in leaderboardData" 
                :key="item.position"
                :class="{
                  'cursor-pointer': item.player_id !== 0 && item.player_id !== gameStore.player?.id && playerRelationStore.findRelationByPlayerId(item.player_id).relation !== 'unknown' && !isPlayerDefeated(item),
                  'border-b border-b-slate-700 h-12 bg-slate-900': true
                }"
                @click="!isPlayerDefeated(item) ? loadPlayer(item.player_id) : null"
              >
                <td class="px-4 py-3 w-10 whitespace-nowrap" :class="isPlayerDefeated(item) ? 'text-slate-300 opacity-30' : 'text-slate-300'">
                  {{ item.position }}
                </td>
                <td class="px-2 py-3 text-center w-12 whitespace-nowrap" :class="isPlayerDefeated(item) ? 'opacity-30' : ''">
                  <img 
                    v-if="item.player_id !== 0 && (item.player_id === gameStore.player?.id || playerRelationStore.findRelationByPlayerId(item.player_id).relation !== 'unknown')" 
                    :src="CDN.countryFlag(getPlayerCountry(item.player_id), '32x24')" 
                    class="h-4 rounded mx-auto" 
                    alt="Country flag"
                  />
                  <span v-else class="text-slate-600">-</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap" :class="isPlayerDefeated(item) ? 'text-slate-400 opacity-30' : 'text-slate-400'">
                  <div v-if="item.player_id !== 0" class="flex items-center gap-2">
                    <div>
                      <PlayerName
                        v-if="item.player_id === gameStore.player?.id || playerRelationStore.findRelationByPlayerId(item.player_id).relation !== 'unknown'"
                        :playerId="item.player_id"
                        :show-patreon-icon="true"
                      />
                      <span v-else class="text-slate-600">Unmet Empire</span>
                    </div>
                    <span v-if="isPlayerDefeated(item)" class="text-xs text-slate-500 italic">
                      (defeated)
                    </span>
                    <span v-if="item.percentage && (item.position === 1 || item.player_id === gameStore.player?.id)" class="text-xs text-slate-400 ml-2">
                      ({{ item.percentage }}%)
                    </span>
                  </div>
                  <span v-else class="text-slate-600">Anonymous Player</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap" :class="isPlayerDefeated(item) ? 'text-slate-400 opacity-30' : 'text-slate-400'">
                  <div v-if="item.player_id !== 0">
                    <span 
                      v-if="getAllianceName(item.player_id) && (item.player_id === gameStore.player?.id || playerRelationStore.findRelationByPlayerId(item.player_id).relation !== 'unknown')"
                      class="text-slate-300 text-sm"
                    >
                      {{ getAllianceName(item.player_id) }}
                    </span>
                    <span v-else-if="item.player_id === gameStore.player?.id || playerRelationStore.findRelationByPlayerId(item.player_id).relation !== 'unknown'" class="text-slate-600 text-sm">
                      No Alliance
                    </span>
                    <span v-else class="text-slate-600 text-sm">
                      Unknown
                    </span>
                  </div>
                  <span v-else class="text-slate-600 text-sm">-</span>
                </td>
                <td class="px-2 py-3 text-center whitespace-nowrap" :class="isPlayerDefeated(item) ? 'opacity-30' : ''">
                  <div v-if="item.player_id !== 0 && isPlayerInactive(item) && !isPlayerDefeated(item)">
                    <GenericIcon
                      type="zzz"
                      tooltip="Inactive"
                      class="inline-block"
                    />
                  </div>
                </td>
                <td v-if="gameStore.combatMode !== 'FFA'" class="px-2 py-3 text-center whitespace-nowrap" :class="isPlayerDefeated(item) || isPlayerInactive(item) ? 'opacity-30' : ''">
                  <div v-if="item.player_id !== 0 && item.player_id !== gameStore.player?.id">
                    <GenericIcon 
                      v-if="!gameStore.canBeAttacked(item.player_id)" 
                      type="shields"
                      tooltip="Protected - Cannot be attacked"
                      class="inline-block"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </StandardCard>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
